import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listMySlots, type Slot } from '../api';
import { useActiveSlotStore } from '../active-slot-store';
import { useAuthStore } from '../auth-store';
import { formatSlotLabel } from '../format';

export type SlotWithLabel = Slot & { label: string; ordinal: number };

/**
 * The slot id to send on PRICE-BEARING requests (`?slotId=` / `buyerSlotId`).
 *
 * Reads the persisted store SYNCHRONOUSLY (it is localStorage-backed, see
 * `active-slot-store.ts`) instead of waiting for the `['my-slots']` query.
 * `useMySlots().activeSlotId` is null until that query resolves, so on a hard
 * reload the first catalog fetch would go out with no slot — the server then
 * falls back to the member's EARLIEST active slot (`pickBuyerSlotId`), caching
 * a wrong price under a null query key before visibly re-pricing.
 *
 * A stale or foreign persisted id is safe: the server re-validates ownership in
 * `resolveMemberBuyerSlot` and falls back. `useMySlots` stays mounted app-wide
 * and repairs the store once the slots land.
 */
export function usePricingSlotId(): string | null {
  return useActiveSlotStore((s) => s.activeSlotId);
}

export type UseMySlotsResult = {
  slots: Slot[];
  activeSlots: Slot[];
  activeSlotsWithLabel: SlotWithLabel[];
  activeSlot: Slot | null;
  activeSlotId: string | null;
  activeSlotLabel: string | null;
  hasAnyActiveSlot: boolean;
  isLoading: boolean;
  setActiveSlotId: (id: string | null) => void;
};

/**
 * Source of truth for the caller's slots + which one is currently active.
 * Hydrates the active-slot store: if the persisted id isn't in `activeSlots`
 * (or none was persisted), default to the earliest active slot.
 */
export function useMySlots(): UseMySlotsResult {
  const token = useAuthStore((s) => s.token);
  const { activeSlotId, setActiveSlotId } = useActiveSlotStore();

  const q = useQuery({
    queryKey: ['my-slots'],
    queryFn: listMySlots,
    enabled: !!token,
    staleTime: 30_000,
  });

  const slots = q.data ?? [];
  const activeSlots = useMemo(
    () =>
      slots
        .filter((s) => s.status === 'active')
        .sort((a, b) => {
          const aa = a.activatedAt ? new Date(a.activatedAt).getTime() : Infinity;
          const bb = b.activatedAt ? new Date(b.activatedAt).getTime() : Infinity;
          return aa - bb;
        }),
    [slots],
  );

  // Hydrate / repair the active-slot selection.
  useEffect(() => {
    if (q.isLoading) return;
    if (activeSlots.length === 0) {
      if (activeSlotId !== null) setActiveSlotId(null);
      return;
    }
    const persisted = activeSlots.find((s) => s.id === activeSlotId);
    if (!persisted) {
      setActiveSlotId(activeSlots[0]!.id);
    }
  }, [q.isLoading, activeSlots, activeSlotId, setActiveSlotId]);

  const activeSlot = activeSlots.find((s) => s.id === activeSlotId) ?? activeSlots[0] ?? null;

  const activeSlotsWithLabel = useMemo<SlotWithLabel[]>(
    () =>
      activeSlots.map((s, i) => ({
        ...s,
        ordinal: i + 1,
        label:
          activeSlots.length === 1
            ? formatSlotLabel(s.slotCode)
            : formatSlotLabel(s.slotCode, i + 1),
      })),
    [activeSlots],
  );

  const activeSlotLabel = activeSlotsWithLabel.find((s) => s.id === activeSlot?.id)?.label ?? null;

  return {
    slots,
    activeSlots,
    activeSlotsWithLabel,
    activeSlot,
    activeSlotId: activeSlot?.id ?? null,
    activeSlotLabel,
    hasAnyActiveSlot: activeSlots.length > 0,
    isLoading: q.isLoading,
    setActiveSlotId,
  };
}
